import { useState } from "react";
import { Copy, Inbox, Loader2, Lock, LockOpen, Plus, Trash2 } from "@fg/icons";
import { driveApi, type DriveItem, type FileRequest } from "@/api/driveApi";
import { useCreateFileRequest, useFileRequests, useUpdateFileRequest } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { formatDateTime, formatRelative, plural } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useToast } from "../toast/ToastProvider";
import { useDialogs } from "./DialogsProvider";

const fieldLabel = "mb-1 block text-xs font-medium text-muted-foreground";

/**
 * "Request files" for one of the user's folders: create an upload link
 * (title, instructions, optional closing date) and manage existing ones —
 * copy link, close / reopen, delete.
 */
export function FileRequestDialog({ folder, onClose }: { folder: DriveItem; onClose: () => void }) {
    const list = useFileRequests(folder.id);
    const create = useCreateFileRequest();
    const change = useUpdateFileRequest();
    const toast = useToast();
    const { confirm } = useDialogs();
    const [formOpen, setFormOpen] = useState(false);
    const [title, setTitle] = useState(folder.name);
    const [instructions, setInstructions] = useState("");
    const [closes, setCloses] = useState("");

    const requests = list.data ?? [];
    const showForm = formOpen || (list.isSuccess && requests.length === 0);
    const today = new Date().toISOString().slice(0, 10);

    const copyLink = async (r: Pick<FileRequest, "token">, created = false) => {
        try {
            await navigator.clipboard.writeText(driveApi.publicFileRequestUrl(r.token));
            toast.success(created ? "File request created — link copied" : "Link copied — anyone with it can upload to this folder");
        } catch {
            toast.error(created ? "File request created, but the browser blocked copying the link." : "Couldn't copy the link — the browser blocked clipboard access.");
        }
    };

    const submit = async () => {
        if (create.isPending) return;
        try {
            const r = await create.mutateAsync({
                folderId: folder.id,
                title: title.trim() || undefined,
                instructions: instructions.trim() || undefined,
                // End of the chosen day, local time.
                expiresAt: closes ? new Date(`${closes}T23:59:59`).toISOString() : null
            });
            setFormOpen(false);
            setInstructions("");
            setCloses("");
            await copyLink(r, true);
        } catch (e) {
            toast.error(e);
        }
    };

    const act = async (r: FileRequest, action: "close" | "reopen" | "delete") => {
        if (action === "delete") {
            const ok = await confirm({
                title: "Delete this file request?",
                message: `The link for “${r.title}” stops working. Files already uploaded stay in the folder.`,
                confirmLabel: "Delete",
                destructive: true
            });
            if (!ok) return;
        }
        try {
            await change.mutateAsync({ id: r.id, action });
            toast.show({ message: action === "delete" ? "File request deleted" : action === "close" ? "File request closed" : "File request reopened" });
        } catch (e) {
            toast.error(e);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            size="lg"
            title={`Request files for “${folder.name}”`}
            description="Anyone with the link can upload files into this folder without signing in. They can't see what's already in it."
            icon={<Inbox className="h-5 w-5 text-primary" />}
            footer={<Button variant="ghost" onClick={onClose}>Done</Button>}
        >
            {showForm ? (
                <form
                    className="space-y-3 rounded-lg border p-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void submit();
                    }}
                >
                    <div>
                        <label htmlFor="fr-title" className={fieldLabel}>Title (shown to people uploading)</label>
                        <Input id="fr-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-autofocus="" />
                    </div>
                    <div>
                        <label htmlFor="fr-instructions" className={fieldLabel}>Instructions (optional)</label>
                        <textarea
                            id="fr-instructions"
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            maxLength={2000}
                            rows={3}
                            placeholder="e.g. Please send your signed forms as PDFs."
                            className="flex w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                        />
                    </div>
                    <div>
                        <label htmlFor="fr-closes" className={fieldLabel}>Stop accepting files after (optional)</label>
                        <Input id="fr-closes" type="date" min={today} value={closes} onChange={(e) => setCloses(e.target.value)} className="w-auto [color-scheme:light] dark:[color-scheme:dark]" />
                    </div>
                    <div className="flex justify-end gap-2">
                        {requests.length > 0 ? <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={create.isPending}>Cancel</Button> : null}
                        <Button type="submit" disabled={create.isPending}>
                            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Create link
                        </Button>
                    </div>
                </form>
            ) : (
                <Button variant="outline" className="w-full" onClick={() => setFormOpen(true)}>
                    <Plus className="h-4 w-4" /> New file request
                </Button>
            )}

            {list.isPending ? (
                <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : list.isError ? (
                <p className="py-4 text-sm text-destructive">Couldn't load this folder's file requests.</p>
            ) : requests.length > 0 ? (
                <ul className="mt-4 divide-y rounded-lg border">
                    {requests.map((r) => {
                        const expired = Boolean(r.expiresAt && Date.parse(r.expiresAt) < Date.now());
                        const state = r.isClosed ? "Closed" : expired ? "Expired" : "Open";
                        const open = state === "Open";
                        return (
                            <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="truncate text-sm font-medium" title={r.title}>{r.title}</span>
                                        <span
                                            className={cn(
                                                "shrink-0 rounded px-1.5 py-px text-[10px] font-medium uppercase tracking-wide",
                                                open
                                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
                                                    : "bg-muted text-muted-foreground"
                                            )}
                                        >
                                            {state}
                                        </span>
                                    </div>
                                    <div className="mt-0.5 text-xs text-muted-foreground">
                                        {r.uploadCount > 0
                                            ? `${plural(r.uploadCount, "file")} received · last ${formatRelative(r.lastUploadAt)}`
                                            : "No files yet"}
                                        {r.expiresAt ? ` · ${expired ? "closed" : "closes"} ${formatDateTime(r.expiresAt)}` : ""}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    {open ? (
                                        <Button size="sm" variant="outline" onClick={() => void copyLink(r)}>
                                            <Copy className="h-3.5 w-3.5" /> Copy link
                                        </Button>
                                    ) : null}
                                    {r.isClosed ? (
                                        <Button size="icon" variant="ghost" title="Reopen" aria-label={`Reopen “${r.title}”`} onClick={() => void act(r, "reopen")} disabled={change.isPending}>
                                            <LockOpen className="h-4 w-4" />
                                        </Button>
                                    ) : (
                                        <Button size="icon" variant="ghost" title="Close" aria-label={`Close “${r.title}”`} onClick={() => void act(r, "close")} disabled={change.isPending}>
                                            <Lock className="h-4 w-4" />
                                        </Button>
                                    )}
                                    <Button size="icon" variant="ghost" title="Delete" aria-label={`Delete “${r.title}”`} onClick={() => void act(r, "delete")} disabled={change.isPending}>
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </Dialog>
    );
}
